<template>
	<div>
		<!-- Fade-in/out transition for the back (close) button -->
		<Transition name="fade">
			<!-- Back button appears when the player is hovered or when the button itself is hovered -->
			<button
				v-show="showBackButton || isHoveringBackButton"
				class="btn--circle close-button"
				@mouseover="isHoveringBackButton = true"
				@mouseleave="isHoveringBackButton = false"
				@click="closeVideo"
				v-focus
			>
				<!-- Left arrow icon -->
				<IconArrow />
			</button>
		</Transition>
		<div
			ref="playerElement"
			class="player-wrapper fullsize-video"
			@mouseover="handleMouseOver"
			@mouseleave="handleMouseLeave"
		>
			<!-- Container used by kwikMotion to mount the player -->
			<div id="kwik-player-fullscreen" class="kwik-player"></div>
		</div>
		<div v-if="showNextEpisodeCountdown" class="next-episode-overlay">
			<div class="next-episode-content">
				<p>{{ t("player.next_episode_in") }} {{ countdown }}s</p>
				<button @click="playNextEpisode">
					{{ t("player.play_next_now") }}
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { ref, nextTick, computed, Ref, onMounted, onBeforeUnmount, watch } from "vue"
	import { useMovieStore } from "../stores/movies"
	import { useSeriesStore } from "../stores/series"
	import { useDeviceManager } from "../stores/deviceManager"
	import { ScreenOrientation } from "@capacitor/screen-orientation"
	import router from "../router/router"
	import IconArrow from "../icons/IconArrow.vue"
	import { Movie } from "../types/Movie"
	import { Series } from "../types/Series"
	import { Episode } from "../types/Episode"
	import { useI18n } from "vue-i18n"
	import { extractVideoUrlFromKwik } from "../utils/media"
	import { onBeforeRouteLeave } from "vue-router"
	import { useVideoProgress } from "../composables/useVideoProgress"
	import { useUserProfiles } from "../composables/useUserProfile"
	import { useTimestampStore } from "../stores/timestamps"
	import { profileList } from "../composables/useListItems"
	import { useTitle } from "@vueuse/core"
	import { KwikMotion } from "../types/Kwik.link"
	import { useDrmStore } from "../stores/drm"
	import { Browser } from "@capacitor/browser"
	import { tryNativePlayer } from "../utils/nativePlayer"
	import { Capacitor } from "@capacitor/core"
	import { CapacitorValorPlayer } from "../../capacitor-valor-player/src"
	import { url } from "../helper"
	import { usePlaybackSession } from "../composables/usePlaybackSession"
	import { toast } from "vue-sonner"
	import { extractDurationFromKwik } from "../utils/media"
	import { App as CapacitorApp } from "@capacitor/app"
	import { useUserStore } from "../stores/user"
	import { useDRM } from "../composables/drm/useDRM"
	const { isWidevineL1, getWidevineLevel } = useDRM()
	const userStore = useUserStore()
	const { t, locale } = useI18n()
	// import { KeySystems } from "../types/Kwik.link" // adjust import path to match your project
	const { fetchVideoFlagStoreByProfile, storeStartedEntity } = useUserProfiles()
	const timestampStore = useTimestampStore()
	const drmStore = useDrmStore()
	const playbackSession = usePlaybackSession()
	let drmData: any = {}
	const nextEpisodeId = ref<string | null>(null)
	const showNextEpisodeCountdown = ref(false)
	const countdown = ref(5)
	const isUsingNativePlayer = ref(false)
	let countdownInterval: number | undefined
	const duration = ref(0)
	const isSafari = computed(() => {
		const userAgent = navigator.userAgent
		return userAgent.includes("Safari") && !userAgent.includes("Chrome") && !userAgent.includes("Chromium")
	})
	const props = withDefaults(
		defineProps<{
			// Content type for the player route: "movie" | "episode"
			type: string
			// Numeric id (drupal internal nid) used to fetch the entity if item is not provided
			id: number | string
			// Optional pre-fetched entity to avoid extra API call
			item?: Movie | Series | Episode | null
		}>(),
		{
			item: null,
		}
	)
	const deviceManager = useDeviceManager()
	const videoUrl = ref<string | null>(null)
	const movieStore = useMovieStore()
	const seriesStore = useSeriesStore()
	const { addToProfileList, removeFromProfileList } = profileList()
	// Controls back button visibility
	const showBackButton: Ref<boolean> = ref(false)
	const isHoveringBackButton = ref(false)
	// Reference for the kwik player container (kept for clarity/extensibility)
	const playerElement = ref<HTMLElement | null>(null)
	// Start time to seek to when the player becomes ready
	const startTime = ref(0)
	// Resolved entity: either the provided prop or the one we fetch
	const fetchedItem = ref<Movie | Series | null>(null)
	const item = ref<Movie | Series | Episode | null>(props.item || null)
	const loading = ref(false)

	const isClosing = ref(false)
	/**
	 * stops the next episode countdown
	 */
	const stopNextEpisodeCountdown = () => {
		if (countdownInterval) {
			clearInterval(countdownInterval)
			countdownInterval = undefined
		}
		showNextEpisodeCountdown.value = false
	}
	/**
	 * Fetch item from API if not provided as prop
	 */
	const fetchItem = async () => {
		if (props.item || loading.value) return

		loading.value = true
		try {
			// Determine endpoint based on route type
			const endpoint: Ref<string> = ref("")
			endpoint.value = props.type === "movie" ? "node/movie" : "node/episode"
			const { locale } = useI18n()
			const api = (await import("../composables/useApi")).default
			// Episodes require field_series include for timestamp lookups
			const extraApiFields =
				endpoint.value === "node/episode"
					? ",field_series,field_series.field_image,field_subtitles,field_subtitles.field_subtitle_file"
					: ",field_subtitles,field_subtitles.field_subtitle_file"
			const response = await api.get(
				`${locale.value}/jsonapi/${endpoint.value}?jsonapi_include=1&include=field_trailer_file,field_image${extraApiFields}`,
				{
					params: {
						"filter[drupal_internal__nid]": props.id,
						"filter[langcode]": locale.value,
						"filter[status]": 1,
					},
				}
			)

			if (response.data.data && response.data.data.length > 0) {
				fetchedItem.value = response.data.data[0]
				useTitle(`${fetchedItem.value?.title} | Valor Movies` || "Valor Movies")
				return fetchedItem.value
			}
		} catch (error) {
			console.error(`Failed to fetch ${props.type} with id ${props.id}:`, error)
			const customError = new Error(t("player.error_generic"))
			customError.render = true
			throw customError
		} finally {
			loading.value = false
		}
	}

	const playNextEpisode = () => {
		if (!nextEpisodeId.value) return
		window.location.href = `/watch/episode/${nextEpisodeId.value}`
	}

	// Compute start time only after the item is resolved (from prop or fetched)
	const time = ref<number>(0)

	watch(
		() => item.value,
		async (resolvedItem) => {
			if (!resolvedItem) return

			// Entity
			const entityType = ref("") // "movie" | "episode"
			const nid = ref(0) // drupal_internal__nid
			const entityId = (resolvedItem as Movie)?.id || (resolvedItem as Episode)?.id
			// console.log("Resolved item in Watch.vue:", resolvedItem)
			if (resolvedItem?.type === "node--movie") {
				entityType.value = "movie"
				nid.value = (resolvedItem as any)?.drupal_internal__nid
				if (!entityId && !nid.value) return
				// Prefer local timestamp store when available
				if (timestampStore.state.timestamps.length > 0) {
					let timestamp: number | undefined = undefined
					for (const element of timestampStore.state.timestamps) {
						if (element.id === nid.value) {
							timestamp = element.timestamp
							break
						}
					}
					// console.log("Found timestamp in local store for movie:", timestamp)
					time.value = timestamp || 0
				} else {
					// Fallback to server-side stored progress
					const res = await fetchVideoFlagStoreByProfile(entityId, entityType.value, "started", undefined)
					const serverTs = res?.[0]?.timestamp
					if (typeof serverTs === "number") {
						timestampStore.addTimestamp(serverTs, entityType.value, nid.value)
					}
					time.value = serverTs || 0
				}
			} else if (resolvedItem?.type === "node--episode") {
				entityType.value = "episode"
				// For episodes, the timestamp is tracked per series; use series id for lookup
				const seriesId = (item.value as any)?.field_series?.id
				const res = await fetchVideoFlagStoreByProfile(item.value.id, entityType.value, "started", seriesId)
				const first = res[0]
				if (first) {
					nid.value = first.drupal_internal__nid
					const epTs = first.timestamp
					if (typeof epTs === "number") {
						timestampStore.addTimestamp(epTs, "node--episode", nid.value)
					}
					time.value = epTs || 0
				} else {
					time.value = 0
				}
			}
			if (item.value?.type === "node--movie") {
				addToProfileList(movieStore.state.watchingMoviesList, item.value, 44)
				// console.log("Computed movie movieStore.state.watchingMoviesList:", movieStore.state.watchingMoviesList)
			} else {
				// console.log("Item is not a movie is the serie:", item.value?.field_series)
				addToProfileList(seriesStore.state.watchingSeriesList, item.value?.field_series, 44)
				// console.log("Computed serie seriesStore.state.watchingSeriesList:", seriesStore.state.watchingSeriesList)
			}

			startTime.value = time.value
		},
		{ immediate: true }
	)

	/**
	 * Handle mouse over event on player, to conditionally show back button.
	 */
	const handleMouseOver = (): void => {
		if (!isHoveringBackButton.value) {
			showBackButton.value = true
		}
	}
	/**
	 * Handle mouse leave event on player, to conditionally hide back button.
	 */
	const handleMouseLeave = (): void => {
		if (isHoveringBackButton.value) {
			// Keep showing the button if hovering the button itself
			return
		}
		showBackButton.value = false
	}

	/**
	 * Safe accessor for the underlying kwikMotion player instance
	 */
	const getPlayer = () => {
		try {
			return window.kwikMotion?.("kwik-player-fullscreen")
		} catch {
			return null
		}
	}

	// Persist playback progress using the composable. It will be called on close.
	const { saveAndDispose, disposePlayer } = useVideoProgress({
		type: props.type,
		id: props.id,
		getPlayer,
		onComputeAndStore: async ({ currentTime }) => {
			const timestampStore = useTimestampStore()
			let flag = "started"
			if (item.value && (item.value as Movie).type === "node--movie") {
				// if the movie remaning time < 180 seconds, mark as completed
				duration.value = extractDurationFromKwik(item.value?.field_kwik_data) ?? 0
				if (duration.value - currentTime <= 180) {
					flag = "completed"
				}
				// Store movie progress
				timestampStore.addTimestamp(
					Math.floor(currentTime),
					(item.value as Movie).type,
					(item.value as any)?.drupal_internal__nid
				)
				await storeStartedEntity(flag, "movie", Math.floor(currentTime), (item.value as Movie).id, undefined)
			} else if ((item.value as Episode)?.type === "node--episode") {
				duration.value = extractDurationFromKwik(item.value?.field_kwik_data) ?? 0
				// if the episode remaning time < 80 seconds, mark as completed
				if (duration.value - currentTime <= 80) {
					flag = "completed"
				}
				// Store episode progress (per series)
				const ep = item.value as Episode
				timestampStore.addTimestamp(Math.floor(currentTime), ep?.type, (ep as any)?.drupal_internal__nid)
				await storeStartedEntity(flag, "episode", Math.floor(currentTime), ep?.id, ep?.field_series?.id)
			}
		},
	})

	/**
	 * Handle page visibility changes (tab switch, minimize, sleep mode)
	 */
	const handleVisibilityChange = async () => {
		if (document.hidden) {
			// Page is hidden - pause player and end session
			const player = getPlayer()
			if (player && typeof (player as any).paused === "function" && !(player as any).paused()) {
				;(player as any).pause()
			}
			await playbackSession.endSession(true)
		} else {
			// Page is visible again - restart session and resume playback
			const player = getPlayer()
			const contentId = (item.value as any)?.drupal_internal__nid || props.id
			const deviceId = await deviceManager.getCurrentDeviceId()
			// console.log("Resuming playback session on visibility change...")

			const sessionStarted = await playbackSession.startSession({
				deviceId,
				contentId,
				onMaxStreamsReached: () => {
					// Show toast and pause player
					if (player && typeof (player as any).paused === "function" && !(player as any).paused()) {
						;(player as any).pause()
					}
				},
			})

			if (sessionStarted && player && typeof (player as any).play === "function") {
				// Resume playback if session started successfully
				;(player as any).play()
				// Restart heartbeat
				playbackSession.startHeartbeat(() => {
					try {
						return (player as any).currentTime() || 0
					} catch {
						return 0
					}
				})
			}
		}
	}

	/**
	 * Handle beforeunload event (tab/window close, browser close)
	 */
	const handleBeforeUnload = async () => {
		// Use sendBeacon for reliable request on page unload
		await playbackSession.endSession(true)
	}

	/**
	 * Handle app state changes (for native platforms)
	 */
	const handleAppStateChange = async (state: { isActive: boolean }) => {
		// Native player manages its own lifecycle, skip for native player
		if (isUsingNativePlayer.value) {
			return
		}

		if (!state.isActive) {
			// App went to background - pause player and end session
			const player = getPlayer()
			if (player && typeof (player as any).paused === "function" && !(player as any).paused()) {
				;(player as any).pause()
			}
			await playbackSession.endSession(true)
		} else {
			// App came to foreground - restart session and resume playback
			const player = getPlayer()
			const contentId = (item.value as any)?.drupal_internal__nid || props.id
			const deviceId = await deviceManager.getCurrentDeviceId()

			const sessionStarted = await playbackSession.startSession({
				deviceId,
				contentId,
				onMaxStreamsReached: () => {
					// Show toast and pause player
					if (player && typeof (player as any).paused === "function" && !(player as any).paused()) {
						;(player as any).pause()
					}
				},
			})

			if (sessionStarted && player && typeof (player as any).play === "function") {
				// Resume playback if session started successfully
				;(player as any).play()
				// Restart heartbeat
				playbackSession.startHeartbeat(() => {
					try {
						return (player as any).currentTime() || 0
					} catch {
						return 0
					}
				})
			}
		}
	}

	onMounted(async () => {
		// Fetch item if not provided as prop
		item.value = await fetchItem()
		// console.log("Final item in Watch.vue after fetch:", item.value)

		// Get content ID for playback session
		const contentId = (item.value as any)?.drupal_internal__nid || props.id
		const deviceId = await deviceManager.getCurrentDeviceId()

		// Check if running on native platform
		const isNativePlatform = Capacitor.isNativePlatform()

		// Only start session for web player - native player will start its own session
		if (!isNativePlatform) {
			// Start playback session for web player
			const sessionStarted = await playbackSession.startSession({
				deviceId,
				contentId,
			})

			// If session failed to start (max streams), exit early
			if (!sessionStarted) {
				setTimeout(() => {
					router.go(-1)
				}, 3000)
				return
			}
		}

		// Add event listeners for tab close, browser close, and visibility changes
		window.addEventListener("beforeunload", handleBeforeUnload)

		// Use different listeners for web vs native platforms
		if (Capacitor.isNativePlatform()) {
			// Native platforms: use app state listener only
			CapacitorApp.addListener("appStateChange", handleAppStateChange)
		} else {
			// Web platforms: use visibility change listener
			document.addEventListener("visibilitychange", handleVisibilityChange)
		}

		// Extract video URL from kwik JSON string if present
		const playbackData = await extractVideoUrlFromKwik((item.value as any)?.field_kwik_data)
		// console.log("Extracted playback data in Watch.vue:", playbackData)
		if (playbackData === null) {
			throw new Error("An error occurred while trying to play this video. Please try again later.")
		}
		const movieSources = playbackData ? playbackData.sources : null

		// Fetch DRM data if needed
		if (playbackData?.isDRM || playbackData.DRMSource) {
			await drmStore.fetchVideoData(item.value.id)
			drmData = drmStore.videoData
		}

		const isIOS = deviceManager.operatingSystem === "ios"
		const isAndroid = deviceManager.operatingSystem === "android"
		const subs = item.value?.field_subtitles || []
		// Try native player first for DRM content on native platforms
		if (isNativePlatform) {
			//playbackData?.isDRM && drmData
			// console.log("Attempting native DRM playback...")

			// Get the video URL (first HLS or DASH source)
			const hlsSources = drmData?.allSignedSources.HLS || []
			const dashSources = drmData?.allSignedSources["mpeg-dashLink"] || []
			// const videoUrl = hlsSources[0]?.Link || dashSources[0]?.Link
			// For Android, prefer DASH
			const videoUrl = isAndroid
				? dashSources[0]?.SignedLink || hlsSources[0]?.SignedLink // Try DASH first
				: hlsSources[0]?.SignedLink || dashSources[0]?.SignedLink // iOS uses HLS

			if (videoUrl) {
				// Mark that we're using native player
				isUsingNativePlayer.value = true

				const nativePlayerOptions = {
					url: videoUrl,
					autoplay: true,
					allowExternalPlayback: false, // Disable AirPlay
					startTime: startTime.value,
					drm: isIOS
						? {
								fairplay: {
									certificateUrl: "https://drm.kwikmotion.com/certificate/fairplay.cer",
									licenseUrl: drmData.fairplay_license,
								},
						  }
						: {
								widevine: {
									licenseUrl: drmData.widevine_license,
								},
						  },
					subtitles: isSafari.value
						? []
						: item.value?.field_subtitles?.data === undefined && item.value?.field_subtitles?.length > 0
						? item.value.field_subtitles.map((subtitle: any) => {
								let localeString = undefined
								if (locale.value === "en") {
									localeString = "English"
								} else if (locale.value === "el") {
									localeString = "Greek"
								}
								return {
									label: subtitle.field_langcode.toUpperCase(),
									language: subtitle.field_langcode,
									url: `${url}${subtitle.field_subtitle_file?.uri.url}`,
									default: subtitle.field_langcode === localeString,
								}
						  })
						: [],
					// tracks:
					// 	item.value?.field_subtitles.data === undefined
					// 		? item.value.field_subtitles.map((subtitle: any) => {
					// 				console.log("Subtitle item:", subtitle.field_langcode, locale.value)
					// 				return {
					// 					kind: "captions",
					// 					label: subtitle.field_langcode.toUpperCase(),
					// 					srclang: subtitle.field_langcode,
					// 					src: `${url}${subtitle.field_subtitle_file?.uri.url}`,
					// 					default: subtitle.field_langcode === locale.value,
					// 				}
					// 		  })
					// 		: [],
				}

				// Add listener for when native player closes
				await CapacitorValorPlayer.addListener("playerClosed", async () => {
					if (deviceManager.operatingSystem === "ios") {
						closeVideo()
					} else {
						if (item.value.type === "node--movie") {
							router.push(`/movie/${item.value?.drupal_internal__nid}`)
						}
					}
				})

				// Add listener for session ended event from native player
				await CapacitorValorPlayer.addListener("sessionEnded", async () => {
					// End the session
					await playbackSession.endSession()
				})

				// Add listeners for playback events
				await CapacitorValorPlayer.addListener("playbackStarted", async () => {
					// Start playback session and heartbeat
					const sessionStarted = await playbackSession.startSession({
						deviceId: await deviceManager.getCurrentDeviceId(),
						contentId: (item.value as any)?.drupal_internal__nid || props.id,
						onMaxStreamsReached: () => {
							// Max streams reached, session won't start
						},
					})

					if (sessionStarted) {
						// Start heartbeat with native player time getter
						playbackSession.startHeartbeat(() => {
							// Time will come from timeUpdate events
							return 0
						})
					}
				})

				await CapacitorValorPlayer.addListener("playbackPaused", async () => {
					// Pause heartbeat but don't end session
					playbackSession.pauseHeartbeat()
				})

				let lastTimestamp = 0
				await CapacitorValorPlayer.addListener(
					"timeUpdate",
					async (data: { currentTime: number; duration: number }) => {
						const currentTime = Math.floor(data.currentTime)
						const duration = Math.floor(data.duration)
						const remainingTime = duration - currentTime

						// Update timestamp every 5 seconds to avoid too many updates
						if (currentTime - lastTimestamp >= 5) {
							lastTimestamp = currentTime

							if (item.value && (item.value as Movie).type === "node--movie") {
								timestampStore.addTimestamp(
									currentTime,
									(item.value as Movie).type,
									(item.value as any)?.drupal_internal__nid
								)

								// Mark as completed if near end
								if (remainingTime <= 180) {
									await storeStartedEntity("completed", "movie", currentTime, (item.value as Movie).id, undefined)
								} else {
									await storeStartedEntity("started", "movie", currentTime, (item.value as Movie).id, undefined)
								}
							} else if ((item.value as Episode)?.type === "node--episode") {
								const ep = item.value as Episode
								timestampStore.addTimestamp(currentTime, ep?.type, (ep as any)?.drupal_internal__nid)

								// Mark as completed if near end
								if (remainingTime <= 80) {
									await storeStartedEntity("completed", "episode", currentTime, ep?.id, ep?.field_series?.id)
								} else {
									await storeStartedEntity("started", "episode", currentTime, ep?.id, ep?.field_series?.id)
								}
								// Show next episode countdown when less than 30 seconds remaining
								// if (remainingTime <= 30 && !showNextEpisodeCountdown.value) {
								// 	const api = (await import("../composables/useApi")).default
								// 	let episodes: string[] = []
								// 	try {
								// 		const res = await api.get(`/jsonapi/node/episode`, {
								// 			params: {
								// 				"filter[field_series.id]": item.value?.field_series?.id,
								// 				"fields[node--episode]": "drupal_internal__nid",
								// 			},
								// 		})
								// 		episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
								// 	} catch (error) {
								// 		console.error("Failed to fetch episodes:", error)
								// 		return
								// 	}
								// 	const currentId = item.value?.drupal_internal__nid
								// 	const currentIndex = episodes.indexOf(currentId)
								// 	if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
								// 		nextEpisodeId.value = episodes[currentIndex + 1]
								// 		showNextEpisodeCountdown.value = true
								// 		countdown.value = 10

								// 		countdownInterval = window.setInterval(() => {
								// 			if (isClosing.value) {
								// 				stopNextEpisodeCountdown()
								// 				return
								// 			}
								// 			countdown.value -= 1
								// 			if (countdown.value <= 0) {
								// 				stopNextEpisodeCountdown()

								// 				if (!isClosing.value && nextEpisodeId.value) {
								// 					// Close native player and navigate
								// 					CapacitorValorPlayer.close()
								// 					window.location.href = `/watch/episode/${nextEpisodeId.value}`
								// 				}
								// 			}
								// 		}, 1000)
								// 	}
								// }
							}
						}
					}
				)

				// Add listener for playback ended event
				await CapacitorValorPlayer.addListener("playbackEnded", async () => {
					// Show next episode countdown only for episodes
					// Don't call stopVideo() here - let the native player handle its own lifecycle
					CapacitorValorPlayer.stopVideo()
					// closeVideo()

					if (!Capacitor.isNativePlatform()) {
						await playbackSession.endSession()
					}

					if ((item.value as Episode)?.type === "node--episode" && !showNextEpisodeCountdown.value) {
						const api = (await import("../composables/useApi")).default
						let episodes: string[] = []
						try {
							const res = await api.get(`/jsonapi/node/episode`, {
								params: {
									"filter[field_series.id]": item.value?.field_series?.id,
									"fields[node--episode]": "drupal_internal__nid",
								},
							})
							episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
						} catch (error) {
							console.error("Failed to fetch episodes:", error)
							return
						}
						const currentId = item.value?.drupal_internal__nid
						const currentIndex = episodes.indexOf(currentId)

						if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
							nextEpisodeId.value = episodes[currentIndex + 1]
							showNextEpisodeCountdown.value = true
							countdown.value = 15

							countdownInterval = window.setInterval(() => {
								if (isClosing.value) {
									stopNextEpisodeCountdown()
									return
								}
								countdown.value -= 1
								if (countdown.value <= 0) {
									stopNextEpisodeCountdown()
									if (!isClosing.value && nextEpisodeId.value) {
										showNextEpisodeCountdown.value = false
										isClosing.value = false
										router.replace(`/watch/episode/${nextEpisodeId.value}`)
										// Navigate to next episode (player will close automatically)
										// window.location.href = `/watch/episode/${nextEpisodeId.value}`
									}
								}
							}, 1000)
						}
					} else {
						// in case of movie, just close the player and go back
						router.go(-1)
					}
				})

				const nativePlayerStarted = await tryNativePlayer(nativePlayerOptions)

				if (nativePlayerStarted) {
					// console.log("Native player started successfully, skipping web player initialization")
					return // Exit early - native player is handling playback
				}
				// else {
				// 	console.log("Native player failed or not available, falling back to web player")
				// }
			}
		}

		// console.log("CAPS", item.value?.field_subtitles)
		const isL1 = await isWidevineL1()
		const widevineLevel = await getWidevineLevel()
		// console.log("Widevine Level:", widevineLevel)
		// console.log("Widevine L1 capability:", isL1)

		const sources = isIOS
			? Object.values(drmData?.allSignedSources.HLS)
					.map((item) => {
						return item.SignedLink ? { src: item.SignedLink } : null
					})
					.flat()
					.filter((item) => item !== null)
			: Object.values(drmData?.allSignedSources)
					.map((source) =>
						source.map((item) => {
							return item.SignedLink ? { src: item.SignedLink } : null
						})
					)
					.flat()
					.filter((source) => {
						if (!source) return false
						// For Widevine DRM, filter based on L1 capability
						if (widevineLevel !== "L3") {
							return true
						} else {
							// Not L1: only keep 576p sources for lower quality
							// return source.src.includes("576p")
							return true
						}
					})
		let localeString = ""

		if (locale.value === "en") {
			localeString = "English"
		} else if (locale.value === "el") {
			localeString = "Greek"
		}
		let options: KwikMotion.Options = {
			autoplay: true,
			airplayPlugin: {
				addButtonToControlBar: true,
			},
			tracks:
				isIOS && isSafari.value
					? []
					: item.value?.field_subtitles?.data === undefined && item.value?.field_subtitles?.length > 0
					? item.value.field_subtitles.map((subtitle: any) => {
							let localeString = undefined
							if (locale.value === "en") {
								localeString = "English"
							} else if (locale.value === "el") {
								localeString = "Greek"
							}
							return {
								kind: "captions",
								label: subtitle.field_langcode.toUpperCase(),
								srclang: subtitle.field_langcode,
								src: `${url}${subtitle.field_subtitle_file?.uri.url}`,
								default: subtitle.field_langcode === localeString,
							}
					  })
					: [],
			muted: false,
			controls: true,
			disablePictureInPicture: true,
			sources: sources,
			// sources: [
			// 	{
			// 		src: "https://valorvod.siliconweb.com/valorvod/smil:itwfcdn/valorvodsiliconweb/2471161-6Y3137JNPVP4DS9.smil/playlist.m3u8",
			// 	},
			// ],
			// src: "https://valorvod.siliconweb.com/valorvod/smil:itwfcdn/valorvodsiliconweb/2495357-03MAJH0FHO67V6R.smil/playlist.m3u8",
			theme: "fantasy", //"default" | "kwikplayer" | "city" | "fantasy" | "forest" | "sea" | string
			spatialNavigation: {
				enabled: true,
				horizontalSeek: true,
			},
			hlsPlugin: {
				hlsjsConfig: {
					// Core buffering for packet loss recovery
					enableWorker: true,
					maxBufferLength: 60, // 60 seconds
					maxMaxBufferLength: 120, // 120 seconds max
					maxBufferSize: 60 * 1000 * 1000, // 60MB

					// Stall recovery (KEY for Firefox packet loss)
					maxBufferHole: 0.5, // Jump over gaps < 0.5s
					nudgeMaxRetry: 3, // Retry nudging 3 times
					nudgeOffset: 0.1, // Nudge by 0.1s
					maxStarvationDelay: 4, // Max 4s starvation
					maxLoadingDelay: 4, // Max 4s loading delay

					// Fragment handling
					maxFragLookUpTolerance: 0.25,
					forceKeyFrameOnDiscontinuity: true,

					// ABR (Adaptive Bitrate) configuration
					abrEwmaFastLive: 3.0,
					abrEwmaSlowLive: 9.0,
					abrEwmaFastVoD: 3.0,
					abrEwmaSlowVoD: 9.0,
					abrEwmaDefaultEstimate: 500000,
					abrBandWidthFactor: 0.95,
					abrBandWidthUpFactor: 0.7,

					// Live streaming
					liveSyncDurationCount: 3,
					liveMaxLatencyDurationCount: 10,
					liveDurationInfinity: false,

					// Subtitles
					enableWebVTT: true,
					enableIMSC1: true,
					enableCEA708Captions: true,

					// DRM
					emeEnabled: true,

					// Chromium-specific (optional)
					levelSwitchStrategy: "smooth", // or 'instant'
				},
			},
		}

		// console.log("OPTIONS DATA", options)
		if (playbackData?.isDRM) {
			options.dashPlugin = playbackData?.isDRM
				? {
						shakaConfig: {
							drm: {
								servers: {
									"com.widevine.alpha":
										// "https://widevine-dash.ezdrm.com/widevine-php/widevine-foreignkey.php?pX=E489A3&token=y9tP1zJQ0eQRx0nfagVruA&smil=2471245-3E0NX4q88B8029E&expires=1764345995",
										// "https://widevine-dash.ezdrm.com/widevine-php/widevine-foreignkey.php?pX=E489A3&token=x6sDN--GLt-9bTtqQsQ5hg&smil=2471245-3E0NX4q88B8029E&expires=1764351855",
										drmData.widevine_license,
									// "com.microsoft.playready":
									// "https://eu-fps.ezdrm.com/api/licenses/a2fffb43-e6ea-4069-81a1-a76210cfd164?token=x6sDN--GLt-9bTtqQsQ5hg&smil=2471245-3E0NX4q88B8029E&expires=1764351855",
									// drmData.fairplay_license,
									"com.apple.fps":
										// "https://eu-fps.ezdrm.com/api/licenses/a2fffb43-e6ea-4069-81a1-a76210cfd164?token=x6sDN--GLt-9bTtqQsQ5hg&smil=2471245-3E0NX4q88B8029E&expires=1764351855",
										drmData.fairplay_license,
								},
							},
						},
				  }
				: undefined
			options.safariHlsPlugin = playbackData?.isDRM
				? {
						drm: {
							certificateUrl: "https://drm.kwikmotion.com/certificate/fairplay.cer",
							keySystem: "com.apple.fps",
							licenseUrl: drmData.fairplay_license,
						},
				  }
				: undefined
		}

		await nextTick()
		// Initialize player and wire up UI interactions
		window.initPlayer("kwik-player-fullscreen", options, () => {
			const player = window.kwikMotion("kwik-player-fullscreen")
			// ccbutton?: {
			//       enabled?: boolean;
			//       className?: string;
			//       controlText?: string;
			//       eventName?: string;
			//       style?: string;
			//       menuItems?: {
			//           label: string;
			//           eventName: string;
			//           style?: string;
			//       }[];
			//   };

			// const player = window.kwikMotion("my-video", {
			// 	ccbutton: { enabled: true, className: "some-class test-class", controlText: "Click Me", eventName: "cclick" },
			// })

			const video = document.querySelector("video")
			if (video) {
				video.addEventListener("webkitendfullscreen", () => {
					// iOS user closed fullscreen video
					// Add your logic here (e.g., resume app UI, track analytics, etc.)
					router.go(-1)
				})
				video.addEventListener("contextmenu", (e) => e.preventDefault())
			}
			// Auto-enter fullscreen on mobile devices
			// if (isMobile) {
			// 	player.ready(() => {
			// 		try {
			// 			player.requestFullscreen()
			// 		} catch (error) {
			// 			console.log("Could not enter fullscreen:", error)
			// 		}
			// 	})
			// }
			player.on("ended", () => {
				closeVideo()
			})

			player.on("webkitendfullscreen", () => {
				// iOS specific: handle exit from fullscreen
				closeVideo()
			})

			player.on("close", () => {
				// alert("closing")
				// console.log("Player close event triggered")
			})
			player.on("volumechange", () => {
				const isMuted = player.muted() // true / false
				// Update your store
				userStore.state.mute = isMuted
			})
			// Add error recovery for iOS
			player.on("error", (_) => {
				const error = new Error(t("player.error_generic"))
				error.render = true
				throw error
				// throw new Error("Player error: " + error)
				// if (isIOS && player.error()?.code === 3) {
				// 	// MEDIA_ERR_DECODE - try to recover
				// 	// console.log("Attempting to recover from decode error...")
				// 	// player.error(null)
				// 	setTimeout(() => {
				// 		player.load()
				// 		player.play()
				// 	}, 100)
				// }
			})

			// player.on("mouseover", () => {
			// 	handleMouseOver()
			// })
			// player.on("mouseleave", () => {
			// 	handleMouseLeave()
			// })
			player.on("play", () => {
				handleMouseLeave()
			})

			player.on("ready", () => {
				// Seek to last known position (debounced to ensure player is fully ready)
				const isMuted = player.muted(false) // true / false
				// Update your store
				userStore.state.mute = isMuted
				setTimeout(() => {
					if (startTime.value > 0) {
						player.currentTime(startTime.value)
					}
				}, 1500)

				// Start heartbeat after player is ready
				playbackSession.startHeartbeat(() => {
					try {
						return player.currentTime() || 0
					} catch {
						return 0
					}
				})
			})
			player.on("timeupdate", async () => {
				if (item.value && (item.value as Movie).type === "node--movie") {
					// Store movie progress
					if (player.remainingTime() <= 180 && !showNextEpisodeCountdown.value) {
						timestampStore.addTimestamp(
							Math.floor(player.currentTime()),
							(item.value as Movie).type,
							(item.value as any)?.drupal_internal__nid
						)
						await storeStartedEntity(
							"completed",
							"movie",
							Math.floor(player.currentTime()),
							(item.value as Movie).id,
							undefined
						)
					}
					if (player.remainingTime() <= 30) {
					}
				} else if ((item.value as Episode)?.type === "node--episode") {
					if (player.remainingTime() <= 30 && !showNextEpisodeCountdown.value) {
						const api = (await import("../composables/useApi")).default
						let episodes: string[] = []
						try {
							const res = await api.get(`/jsonapi/node/episode`, {
								params: {
									"filter[field_series.id]": item.value?.field_series?.id,
									"fields[node--episode]": "drupal_internal__nid,field_season",
									sort: "field_season",
								},
							})
							episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
						} catch (error) {
							console.error("Failed to fetch episodes:", error)
							return
						}
						const currentId = item.value?.drupal_internal__nid
						const currentIndex = episodes.indexOf(currentId)
						if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
							nextEpisodeId.value = episodes[currentIndex + 1]
							showNextEpisodeCountdown.value = true
							countdown.value = 10
							// Store episode progress (per series)
							const ep = item.value as Episode
							timestampStore.addTimestamp(Math.floor(player.currentTime()), ep?.type, (ep as any)?.drupal_internal__nid)
							await storeStartedEntity(
								"completed",
								"episode",
								Math.floor(player.currentTime()),
								ep?.id,
								ep?.field_series?.id
							)
							countdownInterval = window.setInterval(() => {
								if (isClosing.value) {
									stopNextEpisodeCountdown()
									return
								}
								countdown.value -= 1
								if (countdown.value <= 0) {
									stopNextEpisodeCountdown()

									if (!isClosing.value && nextEpisodeId.value) {
										window.location.href = `/watch/episode/${nextEpisodeId.value}`
									}
								}
							}, 1000)
						} else {
							return
						}
					}
				}
			})
		})
	})

	onBeforeUnmount(async () => {
		isClosing.value = true
		stopNextEpisodeCountdown()

		window.removeEventListener("beforeunload", handleBeforeUnload)
		document.removeEventListener("visibilitychange", handleVisibilityChange)

		if (Capacitor.isNativePlatform()) {
			await CapacitorApp.removeAllListeners()
			await CapacitorValorPlayer.removeAllListeners()
		} else {
			await playbackSession.endSession()
		}
	})

	const closeVideo = async () => {
		isClosing.value = true
		// stopNextEpisodeCountdown()

		if (!Capacitor.isNativePlatform()) {
			await playbackSession.endSession()
		}

		if (item.value.type === "node--movie") {
			router.push(`/movie/${item.value?.drupal_internal__nid}`)
		} else if (item.value.type === "node--episode") {
			router.push(`/series/${item.value?.field_series?.drupal_internal__nid}`)
		} else {
			router.go(-1)
		}
	}

	onBeforeRouteLeave(async () => {
		isClosing.value = true
		stopNextEpisodeCountdown()

		if (!Capacitor.isNativePlatform()) {
			await playbackSession.endSession()
		}

		disposePlayer()
		saveAndDispose()
	})
</script>

<style lang="scss">
	#kwik-player-fullscreen,
	.fullsize-video {
		// padding: 0 !important;
		//
		// height: 100vh !important;

		// video {
		// 	border-radius: 0;
		// 	height: 100vh !important;
		// }
	}
	.next-episode-overlay {
		position: fixed;
		bottom: 50px;
		right: 50px;
		background-color: rgba(0, 0, 0, 0.7);
		color: white;
		padding: 10px 20px;
		border-radius: 8px;
		display: flex;
		align-items: center;
		justify-content: space-between;

		button {
			margin-left: 15px;
			background: var(--red);
			color: white;
			border: none;
			padding: 5px 10px;
			border-radius: 4px;
			cursor: pointer;
		}
	}

	.close-button {
		background: black;
		position: absolute;
		top: 20px;
		left: 30px;
		z-index: 10;
		width: 40px;
		height: 40px;

		svg {
			transform: rotate(180deg) scale(0.5) translateX(6px);
		}
		&:hover {
			cursor: pointer;
		}
	}

	.fullsize-video {
		position: relative;
		width: 100vw;
		height: 100vh;
		background: black;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;

		#kwik-player-fullscreen {
			width: 100%;
			max-width: 100vw;
			max-height: 100vh;
		}

		.video-js.vjs-controls-disabled .vjs-text-track-display,
		.video-js.vjs-user-inactive.vjs-playing .vjs-text-track-display {
			bottom: 6em !important;

			@include br.respond-to(860px) {
				bottom: 4em !important;
			}
		}
		.vjs-theme-fantasy .vjs-control-bar,
		.vjs-theme-city .vjs-control-bar {
			position: fixed !important;
			bottom: 0 !important;

			// @include br.respond-to(1560px) {
			// 	bottom: 40px !important;
			// }
		}

		.video-js .vjs-control-bar {
			background: black !important;
		}
	}

	.video-js .vjs-play-progress {
		background-color: var(--red) !important;
	}

	.vjs-kwikplayer-skin.video-js:not(.vjs-podcast) .vjs-menu > ul {
		background-color: #1e1e1e !important;
	}
	.vjs-menu li.vjs-selected {
		background-color: var(--red) !important;
	}
	.vjs-text-track-display {
		.vjs-text-track-cue {
			div {
				background-color: transparent !important;
				text-shadow: 0px 2px 4px black;
			}
		}
	}
	.vjs-context-menu {
		display: none !important;
	}
</style>
